/**
 * roleResolver — single source of truth for "what role is the user
 * effectively operating as right now?"
 *
 * Used by:
 *   • App.js RoleRoute / PublicRoute / ProtectedRoute (pre-existing
 *     copies migrate to import from here in a separate sweep).
 *   • UpgradePrompt component (decides /upgrade vs /pricing).
 *   • Pricing.js (decides which plan list to render).
 *   • Tests pinning the no-cross-role-leakage rule.
 *
 * Rules (in order):
 *   1. URL param `?audience=adi|pdi|learner` overrides everything
 *      (anonymous landing pages — never show learner plans to a PDI
 *      who clicked "See pricing" from `/for-pdis`).
 *   2. Founder mode (`operating_mode` like `demo_adi`, `real_learner`)
 *      always wins over the underlying admin role.
 *   3. Admin with `admin_view_role` set is operating as that role.
 *   4. Otherwise use the user's literal `role`.
 *   5. Anonymous → "learner" (the public-facing default).
 *
 * Always returns one of: "learner" | "adi" | "pdi" | "admin".
 */

export const ROLE_LEARNER = "learner";
export const ROLE_ADI = "adi";
export const ROLE_PDI = "pdi";
export const ROLE_ADMIN = "admin";

/**
 * Extract the founder-mode role from `user.operating_mode`. Returns
 * one of "learner" / "adi" / "pdi" or null if the admin isn't in
 * founder mode.
 *
 * Mirrors App.js's `founderModeRole` exactly so we can ship this util
 * without breaking the existing route guard contract.
 */
export function founderModeRole(user) {
  const om = user?.operating_mode;
  if (!om || !om.includes("_")) return null;
  const parsed = om.split("_", 2)[1] || null;
  if (parsed === "learner" || parsed === "adi" || parsed === "pdi") {
    return parsed;
  }
  return null;
}

/**
 * Resolve the effective role for the current view.
 *
 * @param {object|null} user — auth user or null when anonymous
 * @param {object} [opts]
 * @param {string} [opts.audienceParam] — URL `?audience=` value
 * @returns {"learner"|"adi"|"pdi"|"admin"}
 */
export function resolveEffectiveRole(user, opts = {}) {
  const audience = (opts.audienceParam || "").toLowerCase();
  if ([ROLE_LEARNER, ROLE_ADI, ROLE_PDI].includes(audience)) {
    return audience;
  }
  if (!user) return ROLE_LEARNER;

  const fm = founderModeRole(user);
  if (fm) return fm;

  // Admin authority may arrive as the canonical role or as the
  // legacy owner flag on an instructor-shaped account. Both are
  // server-issued authority; neither should strand the owner outside
  // the Admin Command Centre. Preview/founder mode remains scoped
  // because founder mode was resolved above and admin_view_role is
  // still applied before returning the admin role.
  const isAdminIdentity =
    user.role === ROLE_ADMIN || user.is_admin === true;
  if (isAdminIdentity) {
    const v = user.admin_view_role;
    if (v === ROLE_ADI || v === ROLE_PDI || v === ROLE_LEARNER) return v;
    return ROLE_ADMIN;
  }
  return user.role || ROLE_LEARNER;
}

/**
 * Map the resolved effective role onto the V3 media controller's
 * persona token. Mirrors `media_controller/routes.py::_resolve_persona`
 * exactly so the frontend never disagrees with the backend.
 *
 *   learner → "learner"
 *   adi     → "instructor"
 *   pdi     → "pdi"
 *   admin   → "admin"
 *
 * This is the single source of truth for "what persona shell may
 * this user enter?". Admin-in-preview-mode crosses over to the
 * preview persona via `resolveEffectiveRole` first.
 */
export function personaShellForUser(user, opts = {}) {
  const role = resolveEffectiveRole(user, opts);
  if (role === ROLE_ADI) return "instructor";
  return role;
}

/**
 * Central route-access decision. Used by RoleRoute, nav links,
 * and any component making a persona-gated render decision.
 *
 *   canAccessRoute(["adi","pdi"], user)
 *     → true when the user's EFFECTIVE role (i.e. honoring admin
 *       preview/founder mode) is "adi" or "pdi".
 *
 * Semantic preservation (Feb 27, 2026):
 *   Outside founder mode, an admin's *raw* `user.role === "admin"`
 *   is also honored as an access right. This matches legacy
 *   RoleRoute behaviour where `admin_view_role=adi` is a UI lens
 *   over the admin role rather than a hard scope. Founder mode
 *   (`operating_mode=real_*`/`demo_*`) IS strict — admin is
 *   scoped to the previewed persona only, mirroring the operator
 *   doctrine that founder mode is the "real" persona dry-run.
 *
 * @param {string[]} allowedRoles  e.g. ["adi","pdi","admin"]
 * @param {object|null} user
 * @param {object} [opts]
 * @param {string} [opts.audienceParam]
 * @param {boolean} [opts.allowAdminPassthrough=true] — when true
 *   (default), a plain admin (no preview persona set) is allowed
 *   into ANY route. Matches the lenient default for non-route
 *   consumers like nav cards / pricing audience. Pass `false`
 *   from RoleRoute so plain admin is scoped to routes that
 *   explicitly include "admin" in allowedRoles.
 * @returns {boolean}
 */
export function canAccessRoute(allowedRoles, user, opts = {}) {
  const allowed = (allowedRoles || []).map((r) => (r || "").toLowerCase());
  const effective = resolveEffectiveRole(user, opts);
  if (allowed.includes(effective)) return true;
  // Founder mode is strict — never fall through to raw role.
  const inFounderMode = !!founderModeRole(user);
  if (!inFounderMode && user) {
    // A server-issued is_admin flag is the legacy owner identity
    // equivalent of role="admin". Honor it outside strict founder
    // mode so Return to Admin can reach admin-only routes.
    const rawRole = user.is_admin === true
      ? ROLE_ADMIN
      : (user.role || "").toLowerCase();
    if (rawRole && allowed.includes(rawRole)) return true;
  }
  if (opts.allowAdminPassthrough !== false && effective === ROLE_ADMIN) {
    return true;
  }
  return false;
}

/**
 * Is the resolved role on the instructor side of the platform?
 * ADI + PDI = instructors. Admin previewing as instructor returns
 * true here only when `admin_view_role` is `adi`/`pdi` OR founder
 * mode is `*_adi` / `*_pdi`.
 */
export function isInstructorRole(role) {
  return role === ROLE_ADI || role === ROLE_PDI;
}

export function isLearnerRole(role) {
  return role === ROLE_LEARNER;
}

/**
 * Resolve the canonical membership/upgrade target for the given user.
 * Used by `<UpgradePrompt>` so a single component never sends an
 * instructor into learner pricing copy.
 *
 *   learner → "/upgrade"   (LearnerUpgrade — Premium £9.99/month)
 *   adi/pdi → "/pricing"   (Pricing — instructor plans only)
 *   admin   → "/pricing"   (the page resolves audience itself)
 */
export function membershipPathForUser(user) {
  const role = resolveEffectiveRole(user);
  if (isLearnerRole(role)) return "/upgrade";
  return "/pricing";
}
